import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    markAllNotificationsAsRead,
    markNotificationAsRead,
    subscribeTenantNotifications,
} from '@services/registry';

export function useTenantNotifications({ tenantId, userUid }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!tenantId || !userUid) {
            setRows([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const unsub = subscribeTenantNotifications({
            tenantId,
            userUid,
            onNext: (nextRows) => {
                setRows(nextRows || []);
                setLoading(false);
            },
            onError: () => {
                setRows([]);
                setLoading(false);
            },
            pageSize: 20,
        });
        return () => unsub?.();
    }, [tenantId, userUid]);

    const unreadCount = useMemo(
        () => (rows || []).filter((row) => !Array.isArray(row.readBy) || !row.readBy.includes(userUid)).length,
        [rows, userUid]
    );

    const markOneRead = useCallback(async (notificationId) => {
        if (!tenantId || !userUid || !notificationId) return;
        await markNotificationAsRead({ tenantId, notificationId, userUid });
    }, [tenantId, userUid]);

    const markAllRead = useCallback(async () => {
        if (!tenantId || !userUid) return;
        const unreadIds = (rows || [])
            .filter((row) => !Array.isArray(row.readBy) || !row.readBy.includes(userUid))
            .map((row) => row.id);
        await markAllNotificationsAsRead({ tenantId, notificationIds: unreadIds, userUid });
    }, [tenantId, userUid, rows]);

    return {
        notifications: rows,
        loading,
        unreadCount,
        markOneRead,
        markAllRead,
    };
}
